import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import DialCard from './DialCard';
import apiClient from '../../utils/api';

// Section 2 of the home page: the composable "Dials" — the adjustable metrics
// you're designing. Each pinned metric is a full-width start → now → next →
// target journey that expands into its full trajectory + weekly prediction
// (the retired /twin page, folded into home). Add/remove dials from the metric
// registry; the report card's score & coverage stay independent of what's
// pinned, so pinning never games the grade.

// How accurate have the app's weekly projections been? Pools every metric's
// graded weeks into one plain-English headline — no jargon, self-explaining.
const TrustStrip = ({ predictions }) => {
  const trust = useMemo(() => {
    if (!predictions) return null;
    let graded = 0;
    let hits = 0;
    predictions.forEach((p) => {
      const a = p.accuracy;
      if (a?.n_graded) {
        graded += a.n_graded;
        hits += Math.round(a.hit_rate * a.n_graded);
      }
    });
    const calls = predictions.filter((p) => p.current).length;
    return { graded, hits, calls };
  }, [predictions]);

  if (!trust || (trust.graded === 0 && trust.calls === 0)) return null;

  return (
    <div className="mb-5 rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card px-4 py-3">
      <p className="text-sm font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400 mb-1">
        How accurate are these projections?
      </p>
      {trust.graded > 0 ? (
        <p className="text-sm text-gray-700 dark:text-gray-200">
          Each week Vegetablah predicts where your metrics are heading, then checks itself
          against what actually happened. So far it's landed{' '}
          <span className="font-semibold text-gray-900 dark:text-white">
            {Math.round((trust.hits / trust.graded) * 100)}%
          </span>{' '}
          inside its predicted range across{' '}
          <span className="font-semibold text-gray-900 dark:text-white">{trust.graded}</span>{' '}
          check{trust.graded === 1 ? '' : 's'}.{' '}
          {trust.calls > 0 && (
            <span className="text-gray-500 dark:text-gray-400">
              {trust.calls} new prediction{trust.calls === 1 ? '' : 's'} out this week — graded next Monday.
            </span>
          )}
        </p>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Each week Vegetablah predicts where your metrics are heading, then checks itself
          against what actually happens. {trust.calls} prediction{trust.calls === 1 ? '' : 's'}{' '}
          {trust.calls === 1 ? 'is' : 'are'} out for this week — the first grades post next Monday.
        </p>
      )}
    </div>
  );
};

const DialsSection = ({ refreshKey }) => {
  const [cards, setCards] = useState(null);
  const [byKey, setByKey] = useState(new Map());
  const [targets, setTargets] = useState([]);
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Fire-and-forget: regenerate any stale/missing AI tips in a separate request
  // so the dials render instantly and the coaching lines stream in a beat later.
  // Only runs when the user is actually here looking at them — never in the
  // background, never for logged-out users.
  const refreshSuggestions = useCallback((cardList) => {
    if (!cardList?.some((c) => c.suggestion_stale)) return;
    apiClient
      .post('/api/twin/cards/suggestions')
      .then((res) => {
        setCards((prev) =>
          prev
            ? prev.map((c) =>
                c.metric_key in res.data
                  ? { ...c, suggestion: res.data[c.metric_key], suggestion_stale: false }
                  : c
              )
            : prev
        );
      })
      .catch(() => {
        // Leave the placeholder; a later visit retries. Never blocks the UI.
      });
  }, []);

  const load = useCallback(async () => {
    try {
      const [cardRes, trajRes, targetRes, predRes] = await Promise.all([
        apiClient.get('/api/twin/cards'),
        apiClient.get('/api/twin/trajectory'),
        apiClient.get('/api/twin/targets'),
        apiClient.get('/api/twin/predictions'),
      ]);
      const targetMap = new Map(targetRes.data.map((t) => [t.metric_key, t]));
      const predMap = new Map(predRes.data.map((p) => [p.metric_key, p]));
      const merged = new Map(
        trajRes.data.map((tr) => [
          tr.metric_key,
          { ...tr, target: targetMap.get(tr.metric_key) || {}, prediction: predMap.get(tr.metric_key) || null },
        ])
      );
      setCards(cardRes.data);
      setByKey(merged);
      setTargets(targetRes.data);
      setPredictions(predRes.data);
      refreshSuggestions(cardRes.data);
    } catch (error) {
      toast.error('Failed to load your dials');
    } finally {
      setLoading(false);
    }
  }, [refreshSuggestions]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const setPin = async (metricKey, pinned) => {
    setBusy(true);
    try {
      await apiClient.patch(`/api/twin/cards/${metricKey}`, { pinned });
      const cardRes = await apiClient.get('/api/twin/cards');
      setCards(cardRes.data);
      // keep the targets list's pinned flags in sync for the picker
      setTargets((prev) =>
        prev.map((t) => (t.metric_key === metricKey ? { ...t, pinned } : t))
      );
      // a freshly pinned dial has no tip yet — generate it
      if (pinned) refreshSuggestions(cardRes.data);
    } catch (error) {
      toast.error('Could not update your dials');
    } finally {
      setBusy(false);
    }
  };

  // Metrics available to add: everything with a target that isn't already
  // pinned, minus the mitochondrial index (it owns its own hero card).
  const available = useMemo(
    () => targets.filter((t) => !t.pinned && t.enabled && t.metric_key !== 'mito_index'),
    [targets]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!cards) return null;

  return (
    <section>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Your dials</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            The metrics you're designing — started, now, and where you're headed.
          </p>
        </div>
        <div className="relative shrink-0">
          <button
            onClick={() => setPickerOpen((v) => !v)}
            disabled={available.length === 0}
            className="text-sm font-medium px-3 py-1.5 rounded-lg border border-surface-border dark:border-surface-dark-border text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors bg-transparent"
          >
            + Add a dial
          </button>
          {pickerOpen && available.length > 0 && (
            <div className="absolute right-0 mt-1 w-56 max-h-72 overflow-y-auto rounded-lg border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card shadow-lg z-20 py-1">
              {available.map((t) => (
                <button
                  key={t.metric_key}
                  disabled={busy}
                  onClick={() => {
                    setPin(t.metric_key, true);
                    setPickerOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <TrustStrip predictions={predictions} />

      {cards.length === 0 ? (
        <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-8 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            No dials pinned yet. Add one to start designing a metric.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {cards.map((card) => (
            <DialCard
              key={card.metric_key}
              card={card}
              metric={byKey.get(card.metric_key)}
              onUnpin={(k) => setPin(k, false)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default DialsSection;
